// Shared subscription-tier config used by the subscriber-facing "Choose
// your plan" overlay (components/popup/subscription-plans.tsx), the Profile
// page, the admin Company page, and the admin "Subscription Tiers" editor.
// Backed by the real SubscriptionTiers table (express-server/util/
// subscription-tiers.js) - previously this was a client-only copy persisted
// to localStorage, which drifted from the backend's own hardcoded copy at
// least once (the Pro/Elite rename bug). Every consumer now reads through
// useSubscriptionTiers() instead of a local cache.
import React from 'react';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';

// 'daily' is only for QA plans (hidden, opened to QA companies).
export type BillingCycle = 'monthly' | 'annual' | 'daily';

export interface TierConfig {
    id: string;
    name: string;
    // null for Free, which has no monthly/annual variant.
    billing: BillingCycle | null;
    billingNote: string;
    pricePerMonth: number;
    // The actual amount charged right now - one month's price for a
    // no-commitment tier, the full 12-month commitment for an up-front one.
    minimumGuaranteed: number;
    horizonDays: number;
    users: number;
    userAcl: boolean;
    xeroIntegration: boolean;
    stripeIntegration: boolean;
    advancedFeatures: boolean;
    documents?: boolean;
    highlight: boolean;
    // { [inclusionKey]: pricePerMonth } - the add-ons this tier can buy,
    // from the add-on catalog (server-derived; an annual tier's is the
    // yearly price over 12 months). The flags above mean "included".
    addonPrices?: Partial<Record<AddonKey, number>>;
    // Documents storage allowance in MB (one of STORAGE_SIZES_MB).
    storageMb?: number;
    // The add-ons an admin lets this tier buy (Admin > Subscription Tiers).
    availableAddons?: AddonKey[];
    // Shown in the "Choose your plan" dialog (false = hidden; companies
    // already on it keep it).
    isVisible?: boolean;
    // Card order in the plan dialog, shared by a plan's Monthly and Yearly tiers.
    sortOrder?: number;
    // The plan a tier belongs to - 'pro' for 'pro-monthly' / 'pro-annual'.
    planKey?: string;
}

// Mirrors express-server util/subscription-tiers.js getPlanKey.
export const planKeyOf = (tierId: string) => tierId.replace(/-(monthly|annual)$/, '');

// What a plan can include - mirrors express-server
// util/subscription-inclusions.js. Per tier each one is included (the tier's
// flag), a paid add-on (not included, sold from the add-on catalog - Admin >
// Subscription Add-ons) that each company turns on itself, or off.
export type InclusionKey = 'userAcl' | 'xeroIntegration' | 'stripeIntegration' | 'advancedFeatures' | 'documents';
export const INCLUSIONS: { key: InclusionKey; label: string; description: string }[] = [
    { key: 'userAcl', label: 'User access control', description: 'Roles and permissions for each team member' },
    { key: 'xeroIntegration', label: 'Xero integration', description: 'Sync invoices and transactions from Xero' },
    { key: 'stripeIntegration', label: 'Stripe integration', description: 'Collect invoice payments through Stripe' },
    { key: 'advancedFeatures', label: 'Advanced features', description: 'Advanced forecasting tools' },
    { key: 'documents', label: 'Documents', description: 'Share documents, invoices and reports with tracked links' },
];
export type InclusionMode = 'off' | 'included' | 'addon';

// Capacity add-ons - mirrors express-server util/subscription-inclusions.js.
// Created in Admin > Subscription Add-ons, any number of them; the key says
// what it gives: 'users-5' = 5 more users than the plan's own, 'horizon-365'
// = 365 days more projection horizon, 'storage-500' = 500 MB more Documents storage.
export type CapacityKind = 'users' | 'horizon' | 'storage';
export type AddonKey = InclusionKey | `users-${number}` | `horizon-${number}` | `storage-${number}`;
export const isInclusionKey = (key: string): key is InclusionKey => INCLUSIONS.some(i => i.key === key);
export const parseCapacityKey = (key: string): { kind: CapacityKind; amount: number } | null => {
    const match = /^(users|horizon|storage)-(\d+)$/.exec(key || '');
    return match ? { kind: match[1] as CapacityKind, amount: Number(match[2]) } : null;
};

// Documents storage comes in these fixed sizes (MB) - a plan's own allowance
// and every storage add-on are one of them; a plan with none set gets the
// middle one. Mirrors express-server util/subscription-inclusions.js.
export const STORAGE_SIZES_MB = [100, 250, 500, 750, 1024];
export const DEFAULT_STORAGE_MB = STORAGE_SIZES_MB[Math.floor(STORAGE_SIZES_MB.length / 2)];
export const formatStorage = (mb: number) => (mb >= 1024 && mb % 1024 === 0 ? `${mb / 1024} GB` : `${mb} MB`);

// Horizon add-ons come in these fixed lengths (days) - mirrors express-server
// util/subscription-inclusions.js; the labels are horizon values Company
// Settings saves and the server reads back ("6 months" = 180, "2 years" = 730).
export const HORIZON_STEPS_DAYS = [30, 60, 90, 180, 365];
export const formatHorizon = (days: number) => (days === 180 ? '6 months' : days % 365 === 0 ? `${days / 365} year${days === 365 ? '' : 's'}` : `${days} days`);

export const addonLabel = (key: string): string => {
    const capacity = parseCapacityKey(key);
    if (capacity?.kind === 'users') return `${capacity.amount} more user${capacity.amount === 1 ? '' : 's'}`;
    if (capacity?.kind === 'horizon') return `${formatHorizon(capacity.amount)} more projection horizon`;
    if (capacity?.kind === 'storage') return `${formatStorage(capacity.amount)} more storage`;
    return INCLUSIONS.find(i => i.key === key)?.label ?? key;
};
export const addonDescription = (key: string): string => {
    const capacity = parseCapacityKey(key);
    if (capacity?.kind === 'users') return `Adds ${capacity.amount} user${capacity.amount === 1 ? '' : 's'} to your plan's limit`;
    if (capacity?.kind === 'horizon') return `Adds ${formatHorizon(capacity.amount)} to your projection horizon`;
    if (capacity?.kind === 'storage') return `Adds ${formatStorage(capacity.amount)} to your Documents storage`;
    return INCLUSIONS.find(i => i.key === key)?.description ?? '';
};

// The add-on's per-month price - null when the inclusion is off or included.
export const addonPrice = (tier: TierConfig | null | undefined, key: AddonKey): number | null => {
    if (!tier || (isInclusionKey(key) && tier[key])) return null;
    const price = tier.addonPrices?.[key];
    return price == null ? null : Number(price);
};

export const inclusionMode = (tier: TierConfig | null | undefined, key: AddonKey): InclusionMode => {
    if (!tier) return 'off';
    if (isInclusionKey(key) && tier[key]) return 'included';
    return addonPrice(tier, key) == null ? 'off' : 'addon';
};

// One add-on catalog entry (express-server SubscriptionAddons).
export interface AddonConfig {
    addonKey: AddonKey;
    isOffered: boolean;
    pricePerMonth: number;
    pricePerYear: number;
}

// Every add-on a tier sells - features it doesn't include, then capacity add-ons.
export const offeredAddons = (tier: TierConfig | null | undefined): { key: AddonKey; label: string; description: string }[] => {
    const features = INCLUSIONS.filter(i => inclusionMode(tier, i.key) === 'addon').map(i => ({ key: i.key as AddonKey, label: i.label, description: i.description }));
    const capacity = (Object.keys(tier?.addonPrices || {}) as AddonKey[])
        .filter(key => parseCapacityKey(key) && inclusionMode(tier, key) === 'addon')
        .map(key => ({ key, label: addonLabel(key), description: addonDescription(key) }));
    return [...features, ...capacity];
};

// What an add-on is actually charged per billing period on a tier - per
// month, or 12x it up front on an annual tier.
export const addonCharge = (tier: TierConfig | null | undefined, key: AddonKey): number => {
    const price = addonPrice(tier, key);
    if (!tier || price == null) return 0;
    return price * (tier.billing === 'annual' ? 12 : 1);
};

// Shown instantly while the real fetch completes, so there's no empty/
// loading flash on first render - mirrors the backend's own seed data
// exactly (express-server/util/subscription-tiers.js's SEED_TIERS), never
// read as a fallback-on-error, only as the initial render value.
export const DEFAULT_TIERS: TierConfig[] = [
    {
        id: 'free',
        name: 'Free',
        billing: null,
        billingNote: 'Lose access after 7 days',
        pricePerMonth: 0,
        minimumGuaranteed: 0,
        horizonDays: 7,
        users: 1,
        userAcl: false,
        xeroIntegration: false,
        stripeIntegration: false,
        advancedFeatures: false,
        highlight: false,
        sortOrder: 0,
    },
    {
        id: 'starter-monthly',
        name: 'Pro',
        billing: 'monthly',
        billingNote: 'No commitment',
        pricePerMonth: 99,
        minimumGuaranteed: 99,
        horizonDays: 45,
        users: 1,
        userAcl: false,
        xeroIntegration: true,
        stripeIntegration: false,
        advancedFeatures: false,
        highlight: false,
        sortOrder: 2,
    },
    {
        id: 'starter-annual',
        name: 'Pro',
        billing: 'annual',
        billingNote: '12 months up front',
        pricePerMonth: 79,
        minimumGuaranteed: 948,
        horizonDays: 90,
        users: 2,
        userAcl: true,
        xeroIntegration: true,
        stripeIntegration: false,
        advancedFeatures: false,
        highlight: false,
        sortOrder: 2,
    },
    {
        id: 'pro-monthly',
        name: 'Elite',
        billing: 'monthly',
        billingNote: 'No commitment',
        pricePerMonth: 397,
        minimumGuaranteed: 397,
        horizonDays: 180,
        users: 4,
        userAcl: true,
        xeroIntegration: true,
        stripeIntegration: true,
        advancedFeatures: true,
        highlight: true,
        sortOrder: 1,
    },
    {
        id: 'pro-annual',
        name: 'Elite',
        billing: 'annual',
        billingNote: '12 months up front',
        pricePerMonth: 299,
        minimumGuaranteed: 3588,
        horizonDays: 365,
        users: 4,
        userAcl: true,
        xeroIntegration: true,
        stripeIntegration: true,
        advancedFeatures: true,
        highlight: true,
        sortOrder: 1,
    },
];

// Shared fetch so every consumer reads the same live data instead of each
// doing its own GET - returns { tiers, isLoading, refetch }. refetch is
// what the admin editor calls after a save, and what any other open tab/
// page would need to call to pick up an edit (no push mechanism exists, so
// a change elsewhere is only seen on next mount/refetch).
export const useSubscriptionTiers = () => {
    const [fetchTiers, setFetchTiers] = useApi<{ tiers: TierConfig[] }>();
    const [tiers, setTiers] = React.useState<TierConfig[]>(DEFAULT_TIERS);

    const refetch = React.useCallback(() => {
        setFetchTiers(buildApiRequestConfig(routes.subscriptionTiers.list));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (fetchTiers.data?.tiers) setTiers(fetchTiers.data.tiers);
    }, [fetchTiers.data]);

    return { tiers, isLoading: fetchTiers.isLoading, refetch };
};
